import * as Sentry from '@sentry/react';
import { extractComponentFromErrorStack, extractFilePath, extractErrorDetails } from '../utils/errorParsing';
import { addCompleteContextToSentry } from '../contexts/sentryContext';
import { ViewContext } from '../types/errorTypes';

export function handlePromiseRejection(
  event: PromiseRejectionEvent,
  getViewContext: () => ViewContext
): void {
  Sentry.withScope((scope) => {
    scope.setTag('errorType', 'promiseRejection');
    scope.setLevel('error');

    const viewContext = getViewContext();
    scope.setContext('view', viewContext);

    const reason = event.reason;
    
    let componentFromError: string | null = null;
    let filePath: string | null = null;
    
    if (reason instanceof Error) {
      componentFromError = extractComponentFromErrorStack(reason.stack);
      filePath = extractFilePath(reason.stack);
    }
    
    const detectedComponent = componentFromError || 'Unknown';
    
    if (componentFromError) {
      scope.setTag('component', componentFromError);
    }
    
    if (filePath) {
      scope.setTag('file', filePath);
    }
    
    if (reason instanceof Error && reason.message?.includes('🧪 TEST ERROR')) {
      scope.setTag('testError', 'true');
    } else if (reason && typeof reason === 'object' && 'message' in reason) {
      const reasonMessage = String((reason as any).message || '');
      if (reasonMessage.includes('🧪 TEST ERROR')) {
        scope.setTag('testError', 'true');
      }
    }
    
    // Extract detailed information from the rejection reason
    let errorDetails: Record<string, any> = {};
    let errorToCapture: Error;

    if (reason instanceof Error) {
      errorToCapture = reason;
      errorDetails = extractErrorDetails(reason);
    } else if (reason && typeof reason === 'object') {
      errorDetails = extractErrorDetails(reason);
      errorToCapture = new Error(
        reason.message || reason.toString() || 'Unhandled promise rejection'
      );
      if (reason.stack) {
        errorToCapture.stack = reason.stack;
      }
    } else {
      const reasonStr = String(reason);
      errorToCapture = new Error(`Unhandled promise rejection: ${reasonStr}`);
      errorDetails = {
        reason: reasonStr,
        reasonType: typeof reason
      };
    }

    scope.setContext('promiseRejection', {
      reason: reason,
      reasonType: typeof reason,
      timestamp: new Date().toISOString(),
      detectedComponent,
      filePath
    });

    scope.setContext('errorDetails', errorDetails);
    addCompleteContextToSentry(scope);

    if (reason instanceof TypeError) {
      scope.setTag('errorCategory', 'typeError');
      scope.setContext('typeError', {
        name: reason.name,
        message: reason.message,
        stack: reason.stack
      });
    }

    if (reason && typeof reason === 'object') {
      if ('networkError' in reason || 'graphQLErrors' in reason) {
        scope.setTag('errorCategory', 'graphqlError');
      }
    }

    Sentry.captureException(errorToCapture);
  });
}

